/**
 * PlaybookOverlay
 *
 * Slide-up overlay that shows the full playbook viewer across all workflow nodes.
 * Includes cache status per node and cache management actions.
 * Triggered from the node badge or configure panel.
 */

import { useMemo, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { X, Trash } from '@phosphor-icons/react';
import { Button, Text, Flex, type PlaybookViewerNode, type PlaybookBranchInfo } from '@chatsdet/ui';
import { NODE_TYPE_LABEL, type PlaybookStep } from '@chatsdet/types';
import type { WorkflowNode, WorkflowEdge } from '../../../types/workflow';
import { API_URL } from '../../../config';
import { EditablePlaybookViewer } from './components/EditablePlaybookViewer';
import type { usePlaybookCache } from './hooks/usePlaybookCache';
import { resolveCachePlaybookNodes } from './utils/resolveCachePlaybookNodes';
import { selectPlaybookFetchNodeIds } from './utils/selectPlaybookFetchNodeIds';

interface PlaybookOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  nodes: WorkflowNode[];
  edges?: WorkflowEdge[];
  workflowId?: string | null;
  onCacheDeleted?: () => void;
  onAllCachesDeleted?: () => void;
  onNodeNavigate?: (nodeId: string) => void;
  scrollToNodeId?: string | null;
  playbookCache: ReturnType<typeof usePlaybookCache>;
}

function getNodeTitle(node: WorkflowNode): string {
  const data = (node.data ?? {}) as Record<string, unknown>;
  if (data.label) return data.label as string;
  if (data.customActionTitle) return data.customActionTitle as string;

  switch (node.type) {
    case 'login':
      return (data.credentialName as string) || NODE_TYPE_LABEL.login;
    case 'navigate':
      return (data.pageTitle as string) || (data.url as string) || 'Navigate';
    case 'action':
      return (data.actionDescription as string) || 'Action';
    case 'assertion':
      return (data.description as string) || 'Assertion';
    case 'wait':
      return (data.description as string) || 'Wait';
    case 'condition':
      return (data.conditionDescription as string) || 'Condition';
    case 'setup':
      return (data.setupFlowName as string) || 'Setup';
    default:
      return node.type || 'Step';
  }
}

/**
 * Build branch info map from edges for branch-aware playbook rendering.
 * For each condition node, identifies true/false branch children and convergence points.
 */
function buildBranchInfoMap(
  nodes: WorkflowNode[],
  edges: WorkflowEdge[]
): Map<string, PlaybookBranchInfo> {
  const result = new Map<string, PlaybookBranchInfo>();
  const nodeSet = new Set(nodes.map((n) => n.id));

  // Build outgoing edge map grouped by edge type
  const outgoing = new Map<string, Map<string, string[]>>();
  for (const edge of edges) {
    if (!nodeSet.has(edge.source) || !nodeSet.has(edge.target)) continue;
    if (!outgoing.has(edge.source)) outgoing.set(edge.source, new Map());
    const sourceMap = outgoing.get(edge.source)!;
    const edgeType = edge.edgeType || 'default';
    if (!sourceMap.has(edgeType)) sourceMap.set(edgeType, []);
    sourceMap.get(edgeType)!.push(edge.target);
  }

  // Find all reachable nodes from a set of start nodes
  function findAllReachable(startNodes: string[]): Set<string> {
    const visited = new Set<string>();
    const stack = [...startNodes];
    while (stack.length > 0) {
      const id = stack.pop()!;
      if (visited.has(id)) continue;
      visited.add(id);
      const edges = outgoing.get(id);
      if (!edges) continue;
      for (const targets of edges.values()) {
        for (const t of targets) {
          if (!visited.has(t)) stack.push(t);
        }
      }
    }
    return visited;
  }

  // Find condition nodes
  const conditionNodes = nodes.filter((n) => n.type === 'condition');

  for (const condNode of conditionNodes) {
    const edges = outgoing.get(condNode.id);
    if (!edges) continue;

    const explicitTrue = edges.get('condition_true') ?? [];
    const defaultEdges = edges.get('default') ?? [];
    const trueTargets = explicitTrue.length > 0 ? explicitTrue : defaultEdges;
    const falseTargets = edges.get('condition_false') ?? [];

    const trueReachable = findAllReachable(trueTargets);
    const falseReachable = findAllReachable(falseTargets);

    // Exclusive to true branch (not reachable from false)
    const trueExclusive = new Set<string>();
    for (const id of trueReachable) {
      if (!falseReachable.has(id)) trueExclusive.add(id);
    }

    // Exclusive to false branch
    const falseExclusive = new Set<string>();
    for (const id of falseReachable) {
      if (!trueReachable.has(id)) falseExclusive.add(id);
    }

    // Convergence: nodes reachable from both branches
    const convergence = new Set<string>();
    for (const id of trueReachable) {
      if (falseReachable.has(id)) convergence.add(id);
    }

    // Mark the condition node itself
    if (!result.has(condNode.id)) {
      result.set(condNode.id, {
        type: 'branch-start',
        trueBranchNodeIds: [...trueExclusive],
        falseBranchNodeIds: [...falseExclusive],
      });
    }

    // Mark branch children (closest condition wins — don't overwrite)
    for (const id of trueExclusive) {
      if (!result.has(id)) {
        result.set(id, {
          type: 'branch-child',
          branchLabel: 'true',
          conditionNodeId: condNode.id,
        });
      }
    }
    for (const id of falseExclusive) {
      if (!result.has(id)) {
        result.set(id, {
          type: 'branch-child',
          branchLabel: 'false',
          conditionNodeId: condNode.id,
        });
      }
    }

    // Mark convergence points (first convergence node from either branch)
    // Only mark the direct convergence targets, not everything downstream
    for (const id of convergence) {
      if (!result.has(id)) {
        result.set(id, { type: 'convergence' });
      }
    }
  }

  return result;
}

export function PlaybookOverlay({
  isOpen,
  onClose,
  nodes,
  edges,
  workflowId,
  onCacheDeleted,
  onAllCachesDeleted,
  onNodeNavigate,
  scrollToNodeId,
  playbookCache,
}: PlaybookOverlayProps) {
  const [confirmClearAll, setConfirmClearAll] = useState(false);
  const [isDeletingAll, setIsDeletingAll] = useState(false);

  useEffect(() => {
    if (isOpen) playbookCache.refetch();
  }, [isOpen, playbookCache.refetch]);

  // Collect node IDs whose playbook we should fetch — configJson-authored steps
  // OR a cache row (hasCachedReplay). The cache-row arm is load-bearing under
  // playbook-v3, where the playbook lives ONLY on the row (config_json frozen).
  // See selectPlaybookFetchNodeIds for the rationale; resolveCachePlaybookNodes
  // then swaps the row's playbook_json into the viewer for these nodes.
  const playbookNodeIds = useMemo(() => selectPlaybookFetchNodeIds(nodes), [nodes]);

  // Cache-row-first cutover: render the playbook the agent actually reads. Resolve
  // ONCE here and feed the resolved nodes down, so the step list AND the
  // step↔compiledEntries matching read the SAME playbook (see
  // resolveCachePlaybookNodes for the drift rationale). Falls back to configJson
  // when the row has no playbook.
  const resolvedNodes = useMemo(
    () => resolveCachePlaybookNodes(nodes, playbookCache.effectiveByNodeId),
    [nodes, playbookCache.effectiveByNodeId]
  );

  const branchInfoMap = useMemo(
    () =>
      edges && edges.length > 0
        ? buildBranchInfoMap(nodes, edges)
        : new Map<string, PlaybookBranchInfo>(),
    [nodes, edges]
  );

  const viewerNodes = useMemo((): PlaybookViewerNode[] => {
    const result: PlaybookViewerNode[] = [];
    let stepNum = 0;
    for (let i = 0; i < resolvedNodes.length; i++) {
      const node = resolvedNodes[i];
      if (node.type === 'end') continue;

      stepNum++;
      const data = (node.data ?? {}) as Record<string, unknown>;
      const playbookSteps = (data.playbookSteps as PlaybookStep[] | undefined) || [];

      result.push({
        nodeId: node.id,
        nodeName: getNodeTitle(node),
        nodeType: node.type || 'unknown',
        stepNumber: stepNum,
        playbookSteps: playbookSteps.map((step) => ({
          order: step.order,
          action: step.action,
          target: step.target,
          ...(step.params ? { params: step.params } : {}),
          ...(step.note ? { note: step.note } : {}),
          ...(step.screenshotS3Key
            ? {
                screenshotUrl: `${API_URL}/screenshots/${encodeURIComponent(step.screenshotS3Key)}`,
              }
            : {}),
        })),
        branchInfo: branchInfoMap.get(node.id),
      });
    }
    return result;
  }, [resolvedNodes, branchInfoMap]);

  const totalSteps = viewerNodes.reduce((sum, n) => sum + n.playbookSteps.length, 0);
  const cachedNodeCount = playbookNodeIds.filter((id) =>
    playbookCache.cacheByNodeId.has(id)
  ).length;

  // Escape to close
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setConfirmClearAll(false);
        onClose();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  const handleDeleteAllCaches = async () => {
    setIsDeletingAll(true);
    try {
      await playbookCache.deleteAllCaches();
      onAllCachesDeleted?.();
    } finally {
      setIsDeletingAll(false);
      setConfirmClearAll(false);
    }
  };

  const handleDeleteNodeCache = async (nodeId: string) => {
    await playbookCache.deleteCacheForNode(nodeId);
    onCacheDeleted?.();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Background slide-up */}
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0, transition: { duration: 0.3, ease: [0.32, 0.72, 0, 1] } }}
        exit={{ y: '100%', transition: { duration: 0.25, delay: 0.2, ease: [0.32, 0.72, 0, 1] } }}
        className="absolute inset-0 z-40 bg-background"
      />

      {/* Close button */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1, transition: { duration: 0.2, delay: 0.25 } }}
        exit={{ opacity: 0, scale: 0.8, transition: { duration: 0.15 } }}
        className="absolute top-4 left-4 z-50"
      >
        <Button
          variant="ghost"
          size="sm"
          onPress={onClose}
          className="w-9 p-0"
          aria-label="Close playbook"
        >
          <X size={20} weight="bold" />
        </Button>
      </motion.div>

      {/* Content */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, transition: { duration: 0.25, delay: 0.2 } }}
        exit={{ opacity: 0, transition: { duration: 0.15 } }}
        className="absolute inset-0 z-40 flex flex-col items-center"
      >
        {/* Header */}
        <div className="w-full max-w-2xl px-6 pt-6 pb-4 shrink-0">
          <div>
            <Text weight="semibold" size="lg" className="mb-1">
              Effective playbook
            </Text>
            <Text size="sm" className="text-muted-foreground">
              {viewerNodes.length} node{viewerNodes.length !== 1 ? 's' : ''} &middot; {totalSteps}{' '}
              action{totalSteps !== 1 ? 's' : ''} total
              {cachedNodeCount > 0 && <> &middot; {cachedNodeCount} cached</>}
            </Text>
          </div>
        </div>

        {/* Scrollable playbook viewer */}
        <div className="w-full max-w-2xl flex-1 min-h-0 px-6">
          {playbookCache.isError && (
            <div className="mb-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              The effective playbook preview is unavailable. Running is still allowed; execution
              will resolve the playbook on the server.
            </div>
          )}
          <div className="rounded-lg border border-border bg-card h-full overflow-y-auto">
            {/* Read-only: playbooks are agent-owned (authored by runs, not
                humans). The rich viewer is rendered WITHOUT onUpdateNode, so the
                edit affordances (drag/add/delete/inline-edit) are gated off while
                the full display (cache status, locators, screenshots, branches)
                and per-node/all cache management remain. */}
            <EditablePlaybookViewer
              nodes={resolvedNodes}
              edges={edges}
              cacheByNodeId={playbookCache.cacheByNodeId}
              effectiveByNodeId={playbookCache.effectiveByNodeId}
              onDeleteNodeCache={workflowId ? handleDeleteNodeCache : undefined}
              onNodeNavigate={onNodeNavigate}
              scrollToNodeId={scrollToNodeId}
            />
          </div>
        </div>

        {/* Bottom bar — clear all caches */}
        {workflowId && cachedNodeCount > 0 && (
          <div className="w-full max-w-2xl px-6 py-3 shrink-0">
            {confirmClearAll ? (
              <Flex gap="sm" align="center" justify="center">
                <Text size="xs" className="text-muted-foreground">
                  Clear all cached replays?
                </Text>
                <Button
                  variant="ghost"
                  size="sm"
                  onPress={() => setConfirmClearAll(false)}
                  isDisabled={isDeletingAll}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onPress={handleDeleteAllCaches}
                  isDisabled={isDeletingAll}
                >
                  <Trash size={14} />
                  {isDeletingAll ? 'Clearing...' : 'Confirm'}
                </Button>
              </Flex>
            ) : (
              <Flex justify="center">
                <Button
                  variant="ghost"
                  size="sm"
                  onPress={() => setConfirmClearAll(true)}
                  className="text-muted-foreground hover:text-destructive"
                >
                  <Trash size={14} />
                  Clear all caches
                </Button>
              </Flex>
            )}
          </div>
        )}
      </motion.div>
    </>
  );
}
